import React, { FC } from 'react';
import { usePlayer } from 'providers/player/PlayerContext';
import { buildEpisodeTitle, getMiniPlayerProps, isEpisodeTrailer } from 'utils/fnData';
import { PlayingAssetType } from 'types/Player';
import { useNavigate } from 'react-router';
import { Episode, EpisodeCard } from 'types/Asset';
import { getVodPlayerURL, getVODSeriesEpisodeDetailUrl } from 'utils/fnUrl';
import { useDetailLocationParams } from 'hooks/useDetailLocationParams';
import { VODAssetEntitlementState } from 'types/Entitlement';
import useYouboraContentTracking, { ContentTrackingParams } from 'hooks/useYouboraContentTracking';
import { useAuth } from 'providers/useAuth/AuthContext';
import { StandardCover } from 'types/Cover';
import { CardContext } from 'types/Common';
import { ProgressIndicator } from 'components/ProgressIndicator/ProgressIndicator';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { CardTrackingData } from 'types/Tracking';
import { getVodContentMarker } from 'utils/fnContentMarker';
import { useApp } from 'providers/useApp/AppContext';
import { HUUB_VOD_CATALOG_NAME, useVodWatchable } from 'hooks/useVodWatchable';
import { Card } from 'components/Cards/Card/Card';
import { CardVariant } from 'components/Cards/Card/cardVariants';

export const VODEpisodeCardPresenter: FC<{
    asset: EpisodeCard;
    cardContext: CardContext;
    showDescription?: boolean;
    hideGenre?: boolean;
    onClick?: (event: React.MouseEvent<any>, data: Episode) => void;
    trackingData: CardTrackingData;
}> = ({ asset, cardContext, showDescription, hideGenre = false, onClick, trackingData }) => {
    const { episodeId, image, price, bookmark, isPortrait, duration, description } = asset;
    const title = buildEpisodeTitle(asset.episode_title || asset.title, asset.season_number, asset.episode_number);
    const { isGuest } = useAuth();
    const { appLanguage } = useApp();
    const { config } = useConfig();
    const { isMini, updateMiniPlayerProps } = usePlayer();
    const id = asset?.vod_series_id;
    const vodTerm = asset?.vod_terms?.[0];
    const { isVodAssetWatchable, hasVodCatalog, hasVodAssetPurchase } = useVodWatchable(!isGuest);
    const hasVodTerms = asset?.vod_terms?.length > 0;
    const isEntitled = hasVodTerms
        ? asset?.vod_session_until > 0 || isVodAssetWatchable(asset?.id, asset?.vod_terms)
        : asset?.isWatchable ||
          asset?.vod_session_until > 0 ||
          asset?.vodAssetState === VODAssetEntitlementState.ENTITLED ||
          hasVodCatalog(HUUB_VOD_CATALOG_NAME);
    const vodAssetState = isEntitled ? VODAssetEntitlementState.ENTITLED : asset?.vodAssetState;
    const hasRented = !isGuest && hasVodAssetPurchase(asset?.id);
    const locationParamsPath = useDetailLocationParams();
    const navigate = useNavigate();

    const { handleContentFocus, handleContentFocusOut, handleContentClick, trackContentPlay } = useYouboraContentTracking();

    const onPlayClicked = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const contentIdValue = episodeId || id;

        // All three method calls are needed for episodes
        trackContentPlay(contentIdValue, {
            contentLink: contentIdValue,
            contentLinkType: 'contentId',
        });

        const { seriesSeasonId, seasonId, seriesId } = asset as any;
        // Construct seriesSeasonId from available data if not provided
        // Priority: seriesSeasonId > seasonId > constructed from vod_series_id + season_number
        let effectiveSeasonId = seriesSeasonId || seasonId;

        if (!effectiveSeasonId && asset.vod_series_id && asset.season_number != null) {
            effectiveSeasonId = `${asset.vod_series_id}_S${asset.season_number}`;
        }

        if (effectiveSeasonId) {
            trackContentPlay('null', {
                contentLink: effectiveSeasonId,
                contentLinkType: 'seriesSeasonId',
            });
        }

        if (seriesId || asset.vod_series_id) {
            trackContentPlay('null', {
                contentLink: seriesId || asset.vod_series_id,
                contentLinkType: 'seriesId',
            });
        }

        if (isMini) {
            updateMiniPlayerProps(getMiniPlayerProps(asset, PlayingAssetType.VOD));
        } else {
            navigate(getVodPlayerURL(asset.episodeId));
        }
    };

    const progress = isEpisodeTrailer(asset) ? 0 : (bookmark * 100) / duration;

    const metaData = hideGenre ? undefined : `${asset.genres?.join(', ') || ''}`;

    const getYouboraParams = (): ContentTrackingParams => {
        const contentIdValue = episodeId || id;

        return {
            section: trackingData['data-module-title'] || 'Episode Section',
            sectionOrder: trackingData['data-stripe-position'] || 1,
            column: trackingData['data-card-position'] || 1,
            row: 1,
            contentId: contentIdValue,
            contentLink: contentIdValue,
            contentLinkType: 'contentId',
        };
    };

    const handleCardFocus = () => {
        handleContentFocus(getYouboraParams());
    };

    const handleCardFocusOut = () => {
        handleContentFocusOut();
    };

    const handleCardClick = (e: React.MouseEvent) => {
        handleContentClick(getYouboraParams());
        if (onClick) {
            onClick(e, asset as unknown as Episode);
        }
    };

    const contentTrackingProps = {
        onMouseEnter: handleCardFocus,
        onMouseLeave: handleCardFocusOut,
        onClick: handleCardClick,
    };

    const cover: StandardCover = {
        uuid: id + episodeId,
        id,
        title,
        image,
        landscapeImage: image,
        path: getVODSeriesEpisodeDetailUrl(asset, locationParamsPath, appLanguage),
        metaData,
        shortDescription: description,
        circle: false,
        landscape: !isPortrait,
        assetType: asset.type,
        rawData: asset,
        contentMarker: getVodContentMarker(price, vodAssetState, asset?.vod_session_until, vodTerm, hasRented),
        renderPlayIcon: !isGuest && vodAssetState === VODAssetEntitlementState.ENTITLED,
        isEntitled: !isGuest && vodAssetState === VODAssetEntitlementState.ENTITLED,
        progressBar: <ProgressIndicator percentage={progress < config.app_config.player_settings.bookmark_threshold ? progress : 0} />,
        onPlayIconClicked: onPlayClicked,
        ...(onClick && { onClick }),
    };

    if (cardContext !== CardContext.STRIPE) {
        return <Card variant={CardVariant.PORTRAIT} context={cardContext} asset={cover} {...trackingData} {...contentTrackingProps} />;
    }

    return isPortrait ? (
        <Card variant={CardVariant.PORTRAIT} context={cardContext} asset={cover} {...trackingData} {...contentTrackingProps} />
    ) : (
        <Card
            variant={CardVariant.LANDSCAPE}
            context={cardContext}
            asset={showDescription ? cover : { ...cover, metaData: undefined, shortDescription: undefined }}
            showDescription={showDescription}
            {...trackingData}
            {...contentTrackingProps}
        />
    );
};
